<script setup lang="ts">
	import { ref, Ref, computed } from "vue";

	// Composables
	import { useQuery } from "@/lib/query_cache/useQuery";
	import { trackEvent } from "@/lib/analytics/useAnalytics";

	// Components
	import PlanetPOPRTable from "./PlanetPOPRTable.vue";

	// Typoes & Interfaces
	import { IPopulationReport } from "@/features/api/gameData.types";

	// UI
	import { PButton } from "@/ui";
	import { NModal } from "naive-ui";

	const {
		planetNaturalId,
		buttonSize = "md",
		buttonText = "POPR",
	} = defineProps<{
		planetNaturalId: string;
		buttonSize?: "sm" | "md";
		buttonText?: string;
	}>();

	const showPOPRModal: Ref<boolean> = ref(false);
	const buttonLoading: Ref<boolean> = ref(false);
	const buttonDisabled: Ref<boolean> = ref(false);
	const poprData: Ref<IPopulationReport | null> = ref(null);

	async function loadData(planetNaturalId: string): Promise<void> {
		buttonLoading.value = true;

		trackEvent("popr_load", { planetNaturalId });

		try {
			await useQuery("GetPlanetLastPOPR", {
				planetNaturalId: planetNaturalId,
			})
				.execute()
				.then((data: IPopulationReport) => {
					poprData.value = data;
					showPOPRModal.value = true;
				})
				.finally(() => {
					buttonLoading.value = false;
				});
		} catch {
			buttonDisabled.value = true;
		}
	}

	const compButtonText = computed(() =>
		!buttonDisabled.value ? buttonText : "No POPR"
	);

	const buttonType = computed(() =>
		!buttonDisabled.value ? "primary" : "error"
	);
</script>

<template>
	<n-modal
		v-model:show="showPOPRModal"
		preset="card"
		:title="`Latest Population Report: ${planetNaturalId}`"
		class="max-w-[600px]">
		<PlanetPOPRTable
			v-if="poprData"
			:planet-natural-id="planetNaturalId"
			:popr-data="poprData" />
	</n-modal>
	<PButton
		:size="buttonSize"
		:loading="buttonLoading"
		:disabled="buttonDisabled"
		:type="buttonType"
		@click="loadData(planetNaturalId)">
		{{ compButtonText }}
	</PButton>
</template>
